import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { apiFetch } from '../lib/api.js'
import { TwemojiImg } from '../lib/twemoji.jsx'
import { GAME_DEFAULTS } from '../lib/constants.js'
import HostHeader from '../components/ui/HostHeader.jsx'
import HostSideNav from '../components/ui/HostSideNav.jsx'
import { pageBg as podiumBg } from '../lib/images'

const POLL_MS = 5000

const STAND_CONFIG = {
  1: { medal: '🥇', podiumH: 'h-36', bg: 'bg-amber-400', border: 'border-amber-300/60', rankText: 'text-amber-200', delay: 100 },
  2: { medal: '🥈', podiumH: 'h-24', bg: 'bg-slate-400',  border: 'border-slate-300/50',  rankText: 'text-gray-200',  delay: 400 },
  3: { medal: '🥉', podiumH: 'h-16', bg: 'bg-amber-700', border: 'border-amber-600/50', rankText: 'text-amber-300', delay: 700 },
}

function PodiumStand({ kart, rank }) {
  const [visible, setVisible] = useState(false)
  const cfg = STAND_CONFIG[rank]

  useEffect(() => {
    const t = setTimeout(() => setVisible(true), cfg.delay)
    return () => clearTimeout(t)
  }, [cfg.delay])

  return (
    <div
      className={`flex flex-col items-center transition-all duration-700 ease-out
        ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}
    >
      {/* Emoji véhicule sur fond cercle blanc */}
      <div
        className="w-16 h-16 rounded-full bg-white flex items-center justify-center mb-2"
        style={{ border: '3px solid white', boxShadow: '0 0 14px rgba(255,255,255,0.75), 0 4px 12px rgba(0,0,0,0.4)' }}
      >
        <TwemojiImg emoji={kart.type_vehicule} size={40} />
      </div>
      {/* Marche du podium avec pseudo inscrit dessus */}
      <div
        className={`w-24 sm:w-28 ${cfg.podiumH} ${cfg.bg} border ${cfg.border} rounded-t-xl flex flex-col`}
        style={{ boxShadow: '0 8px 32px rgba(0,0,0,0.55), 0 2px 8px rgba(0,0,0,0.45)' }}
      >
        <div className="px-2 pt-2 pb-1 text-center">
          <p className="text-white font-semibold text-xs leading-tight truncate">{kart.pseudo}</p>
        </div>
        <div className="flex-1 flex items-center justify-center">
          <span className="text-3xl">{cfg.medal}</span>
        </div>
      </div>
    </div>
  )
}

export default function PodiumPage() {
  const { code } = useParams()
  const navigate  = useNavigate()

  const [game, setGame]       = useState(null)
  const [karts, setKarts]     = useState([])
  const [nbCases, setNbCases] = useState(20)
  const pollingRef            = useRef(null)

  useEffect(() => {
    apiFetch(`/api/games/${code}`)
      .then(setGame)
      .catch(() => navigate('/', { replace: true }))
  }, [code])

  const fetchPositions = useCallback(async () => {
    try {
      const data = await apiFetch(`/api/games/${code}/positions`)
      setKarts(data.karts)
      setNbCases(data.nb_cases)
    } catch (_) {}
  }, [code])

  useEffect(() => {
    fetchPositions()
    pollingRef.current = setInterval(fetchPositions, POLL_MS)
    return () => clearInterval(pollingRef.current)
  }, [fetchPositions])

  const ranked = useMemo(() => (
    [...karts].sort((a, b) => {
      const aF = a.statut === 'finished'
      const bF = b.statut === 'finished'
      if (aF && !bF) return -1
      if (!aF && bF) return 1
      if (aF && bF) return new Date(a.finished_at ?? 0) - new Date(b.finished_at ?? 0)
      return ((b.tour_actuel - 1) * nbCases + b.position_case) -
             ((a.tour_actuel - 1) * nbCases + a.position_case)
    })
  ), [karts, nbCases])

  function handleGameUpdate(type, data) {
    if (data?.statut) setGame(g => ({ ...g, statut: data.statut }))
  }

  const statut  = game?.statut ?? 'finished'
  const bgStyle = { backgroundImage: `url(${podiumBg})` }

  return (
    <div className="h-screen flex flex-col select-none bg-gray-900">
      <HostHeader
        code={code}
        deckTitle={game?.decks?.titre ?? '…'}
        statut={statut}
        playerCount={karts.length}
        maxPlayers={game?.config?.max_joueurs ?? GAME_DEFAULTS.MAX_PLAYERS}
        onGameUpdate={handleGameUpdate}
      />

      <div className="flex flex-1 min-h-0 bg-cover bg-center sm:bg-top bg-no-repeat" style={bgStyle}>
        <HostSideNav
          code={code}
          active="podium"
          statut={statut}
          deckTitle={game?.decks?.titre ?? ''}
          onGameUpdate={handleGameUpdate}
        />

        <main className="flex-1 overflow-y-auto pt-28 px-6 pb-20 sm:pb-6">

          {ranked.length === 0 ? (
            <div className="flex items-center justify-center h-full">
              <div className="flex flex-col items-center gap-3 bg-gray-900/80 backdrop-blur-sm rounded-2xl p-8">
                <svg className="animate-spin h-8 w-8 text-white/70" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
                </svg>
                <p className="text-white/70 text-sm">Chargement…</p>
              </div>
            </div>
          ) : (
            <>
              {/* Podium top 3 */}
              <div className="bg-gray-900/40 backdrop-blur-sm border border-white/10 rounded-2xl max-w-lg mx-auto mb-6 pt-6 pb-2">
                <div className="flex items-end justify-center gap-2 sm:gap-4">
                  {ranked[1] && <PodiumStand kart={ranked[1]} rank={2} />}
                  {ranked[0] && <PodiumStand kart={ranked[0]} rank={1} />}
                  {ranked[2] && <PodiumStand kart={ranked[2]} rank={3} />}
                </div>
              </div>

              {/* Classement complet */}
              <div className="bg-gray-900/80 backdrop-blur-sm border border-white/10 rounded-2xl overflow-hidden max-w-lg mx-auto">
                <p className="text-white text-[10px] uppercase tracking-widest text-center py-2 border-b border-white/5">
                  Classement complet
                </p>
                {ranked.map((k, i) => {
                  const cfg = STAND_CONFIG[i + 1]
                  return (
                    <div
                      key={k.id}
                      className="flex items-center gap-3 px-4 py-3 border-b border-white/5 last:border-0"
                    >
                      <span className={`text-base font-black w-8 shrink-0 ${cfg ? cfg.rankText : 'text-white/35'}`}>
                        {cfg ? cfg.medal : `${i + 1}e`}
                      </span>
                      <TwemojiImg emoji={k.type_vehicule} size={28} />
                      <span className="text-white font-medium text-sm truncate flex-1">{k.pseudo}</span>
                      <div className="flex items-center gap-2 shrink-0 text-xs text-white/70 font-mono">
                        <span>{k.correctes ?? 0} ✅</span>
                        <span>{k.timeouts ?? 0} ⌛</span>
                        <span>{k.incorrectes ?? 0} ❌</span>
                      </div>
                    </div>
                  )
                })}
              </div>
            </>
          )}
        </main>
      </div>
    </div>
  )
}
